/**
 * Product data lookup Card — BLU-432 Phase B step 15 (PLAN §9 + §9.10).
 *
 * Per-org mapping config for Shopify product metafields → IMS print-file
 * purposes. Rendered between Shopify Integration and Xero Integration
 * cards in OrganizationSettings.tsx.
 *
 * Critical implementation rules (PLAN §9.1 v3.1 + §9.8 v3):
 *
 *   1. Every <Button> below has `type="button"` explicitly. This Card
 *      is RENDERED INSIDE the parent `<form>` in OrganizationSettings.tsx
 *      (line 417); a missing type would default to `submit` and trigger
 *      the parent's global org-update mutation. Audit any new button in
 *      this file by searching for `type="button"`.
 *
 *   2. Mapping-row state is INTENTIONALLY local to this card and NEVER
 *      participates in the parent page's `hasChanges` aggregator (line
 *      ~1037-1072 of OrganizationSettings.tsx). Row Saves use TanStack
 *      Query mutations; the global Save bar stays for Company/Org/
 *      Shopify/Xero fields only.
 *
 *   3. Permission gate: this Card returns `null` when the active user
 *      lacks `Permission.OrgSettingsRead`. That covers BOTH the legit
 *      "no access" case AND the 3PL cross-org-viewing case (PLAN §9.10) —
 *      when a 3PL operator's session is on a client location, the
 *      backend RLS hides the client's mappings and the UI mirrors that
 *      boundary by not rendering the Card at all (no banner, no empty
 *      state, no "denied" message). NOTE: this gate is sufficient for
 *      the current single-active-org session model. Once BLU-367 wires
 *      cross-org settings VIEWING (admin-of-org-A viewing org-B's
 *      settings without re-auth), this guard needs an additional
 *      `targetOrgId !== sessionOrgId → null` check — filed as a
 *      follow-up.
 *
 *   4. Microcopy: curly quotes via &rsquo;, no em-dashes in operator-
 *      visible strings (DESIGN.md §17 + flawless-typography skill rules).
 *
 *   5. All toast IDs are deduplicated via `{ id: ... }` so repeated
 *      errors don't stack indefinitely (craft-checklist resilience
 *      rule + DESIGN.md §15.5).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clipboard,
  Info,
  Layers,
  Loader2,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
// BLU-536 / Phase 56.1 Plan 07 — ALL purpose metadata (union alias, labels,
// help, placeholders, grouping, recommended seeds) is single-sourced in
// `./product-data-lookup/purposes`, which itself derives the union + the
// variant-scope set from @threadheads/shared. No local purpose unions here.
import {
  ADVANCED_GROUPS,
  ADVANCED_PURPOSES,
  CORE_PURPOSES,
  PURPOSE_HELP,
  PURPOSE_LABEL,
  PURPOSE_SHORT_LABEL,
  VARIANT_PURPOSES,
  type Purpose,
} from './product-data-lookup/purposes';
// BLU-460 follow-up — picker + per-SKU Test sub-components live in their own
// files for readability; this Card orchestrates the discovery query + wires
// the rows.
import {
  MetafieldPicker,
  type DiscoveredMetafieldRow,
  type DiscoveryStatus,
} from './product-data-lookup/MetafieldPicker';
import { SkuTestSection } from './product-data-lookup/SkuTestSection';
import { useStaggeredTestPhase } from './product-data-lookup/useStaggeredTestPhase';
// BLU-536 Plan 08 — shared band primitive (single-sourced palette + a11y
// shell), the card-level dual-aware SKU preview, and the read-only Design
// Shape map digest.
import { ResultBand, type BandKind } from './product-data-lookup/ResultBand';
import {
  SkuPreviewPanel,
  type SavedMappingsByPurpose,
} from './product-data-lookup/SkuPreviewPanel';
import { DesignShapeMapSummary } from './product-data-lookup/DesignShapeMapSummary';
// BLU-461 — Phase B sync architecture pivot. The per-row Re-sync UI is
// replaced by a card-top SweepStatusBlock that polls the cron-driven
// sweep state and exposes the admin-only "Sweep now" button.
import { SweepStatusBlock } from './product-data-lookup/SweepStatusBlock';
// Feature A — one-click Shopify metafield-definition provisioning. Renders
// card-top (admins only) as the single primary onboarding CTA; on first run
// it REPLACES the EmptyState's "Use recommended setup" button.
import { CreateDefinitionsBlock } from './product-data-lookup/CreateDefinitionsBlock';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Same regex as the DB CHECK constraint and the router's Zod schema.
const NS_KEY_REGEX = /^[a-z][a-z0-9_]{0,63}$/;

// Toast IDs — deduplicated so repeated errors replace rather than stack
// (DESIGN.md §15.5 + craft-checklist).
const TOAST_ID = {
  upsertError: 'mapping-upsert-error',
  deleteError: 'mapping-delete-error',
  testError: 'mapping-test-error',
  copyError: 'mapping-copy-error',
} as const;

// Motion timings (PLAN §9.4 v3.1 P0-3) live in the shared hook at
// `./product-data-lookup/useStaggeredTestPhase.ts` so the bulk TestButton
// below and the per-SKU SkuTestSection consume one source of truth.

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function ProductDataLookupCard() {
  const { can, isAdmin } = useRole();

  // PLAN §9.10 cross-org hide AND §9.5 RBAC layer alignment in one check.
  if (!can('org_settings.read')) {
    return null;
  }

  return <ProductDataLookupCardInner admin={isAdmin()} />;
}

// ---------------------------------------------------------------------------
// Inner component — split so the permission gate above keeps the early-return
// trivial and we can grab the mappings list without an extra wrapper.
// ---------------------------------------------------------------------------

interface InnerProps {
  admin: boolean;
}

function ProductDataLookupCardInner({ admin }: InnerProps) {
  const utils = trpc.useUtils();

  const mappingsQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, {
    // Avoid the parent OrganizationSettings page's spinner stealing focus
    // on every nav-back; cache through.
    staleTime: 60_000,
  });

  // BLU-460 follow-up — discover available Shopify metafields once per
  // Card mount. Used by MetafieldPicker to populate the dropdown. The
  // backend handler runs THREE sources in parallel via `Promise.allSettled`
  // and returns one merged, deduped result — partial failure surfaces with
  // whichever sources succeeded:
  //   - `listMetafieldDefinitions(PRODUCT)`        (formal)
  //   - `listMetafieldDefinitions(PRODUCTVARIANT)` (formal — BLU-1018)
  //   - `discoverProductMetafields`                (informal, products only)
  //
  // BLU-1018 added the variant owner type because most purposes in this
  // card are variant-scoped and the two mockup-preview purposes are
  // variant-ONLY, so the product-only walk left their pick-list empty.
  //
  // No staleTime: discovery can change as the operator adds products to
  // Shopify, and the call stays cheap (three small settings-only GraphQL
  // requests, not a sync-loop cost). Re-renders are rare anyway.
  const discoveryQuery = trpc.shopifyMetafieldMappings.discoverMetafields.useQuery(undefined, {
    retry: 0, // We surface the errored state via UI; auto-retry would mask it.
  });
  const discoveryStatus: DiscoveryStatus = discoveryQuery.isLoading
    ? 'loading'
    : discoveryQuery.isError
    ? 'errored'
    : (discoveryQuery.data?.length ?? 0) === 0
    ? 'empty'
    : 'loaded';
  const discoveredRows: ReadonlyArray<DiscoveredMetafieldRow> = useMemo(
    () => (discoveryQuery.data ?? []) as ReadonlyArray<DiscoveredMetafieldRow>,
    [discoveryQuery.data],
  );
  const handleRetryDiscovery = useCallback(() => {
    void discoveryQuery.refetch();
  }, [discoveryQuery]);

  const upsertMutation = trpc.shopifyMetafieldMappings.upsert.useMutation({
    onSuccess: () => {
      void utils.shopifyMetafieldMappings.list.invalidate();
    },
    onError: (error) => {
      // PLAN §9.7 — Save failures get a PERSISTENT toast (no auto-dismiss).
      // ID dedup keeps repeated failures from stacking.
      toast.error(error.message, { duration: Infinity, id: TOAST_ID.upsertError });
    },
  });

  const deleteMutation = trpc.shopifyMetafieldMappings.delete.useMutation({
    onSuccess: () => {
      void utils.shopifyMetafieldMappings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message, { duration: Infinity, id: TOAST_ID.deleteError });
    },
  });

  // The sequenced "Use recommended setup" flow (BLU-536 Plan 08) was removed
  // with Feature A: the CreateDefinitionsBlock above the EmptyState is the
  // single first-run CTA for admins, so the core-3 seeding path had become
  // unreachable dead code. Manual per-row setup remains for design-code
  // stores ("Or configure manually").

  // Stable reference for the `mappings` array — TanStack returns a fresh
  // reference on every render when `data` is undefined, which would invalidate
  // the configuredByPurpose useMemo's deps unnecessarily.
  const mappings = useMemo(
    () => mappingsQuery.data ?? [],
    [mappingsQuery.data],
  );
  const isLoading = mappingsQuery.isLoading;

  const configuredByPurpose = useMemo(() => {
    // Keyed by the full 11-purpose union (Purpose = shared MetafieldPurpose).
    const map = new Map<Purpose, (typeof mappings)[number]>();
    for (const m of mappings) {
      map.set(m.purpose, m);
    }
    return map;
  }, [mappings]);

  // BLU-536 Plan 08 — saved ns+key per persisted purpose, threaded into the
  // card-level SkuPreviewPanel (anchor mapping + "which metafield to fix"
  // naming on flagged values).
  const savedMappingsByPurpose: SavedMappingsByPurpose = useMemo(() => {
    const map = new Map<Purpose, { namespace: string; key: string }>();
    for (const m of mappings) {
      map.set(m.purpose, { namespace: m.namespace, key: m.key });
    }
    return map;
  }, [mappings]);

  // Manual-entry expansion for the empty state. Starts collapsed; "Or
  // configure manually" opens it.
  const [manualOpen, setManualOpen] = useState(false);

  // BLU-536 Plan 07 — "Advanced print mapping" disclosure. Collapsed by
  // default; auto-expands ONCE, when the mappings list first resolves with
  // at least one advanced purpose configured (initial-state only — a
  // controlled disclosure that re-derived `open` on every save/delete would
  // fight the operator mid-edit). After that one-shot init the operator's
  // toggle is the only writer.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedInitDone = useRef(false);
  useEffect(() => {
    if (advancedInitDone.current || isLoading) return;
    advancedInitDone.current = true;
    setAdvancedOpen(
      ADVANCED_PURPOSES.some((purpose) => configuredByPurpose.has(purpose)),
    );
  }, [isLoading, configuredByPurpose]);

  // Live count for the trigger badge (unlike auto-expand, the count SHOULD
  // track saves/deletes in real time).
  const configuredAdvancedCount = useMemo(
    () =>
      ADVANCED_PURPOSES.filter((purpose) => configuredByPurpose.has(purpose))
        .length,
    [configuredByPurpose],
  );

  // BLU-432 accordion pass — each mapping is now a collapsed accordion row
  // (mirrors the Settings → Production General/Advanced tabs: `type="multiple"`,
  // one controlled value array per accordion, item `value` = purpose). Rows
  // start COLLAPSED so the card reads as a compact index instead of a wall of
  // mostly-empty fields. Core and advanced are separate accordions so each
  // `onValueChange` only ever reports its own item set.
  const [openCore, setOpenCore] = useState<string[]>([]);
  const [openAdvanced, setOpenAdvanced] = useState<string[]>([]);
  // Deep-link nicety: a row reports a "keep me open" hint while it holds
  // unsaved edits or an invalid namespace/key, so a validation error can never
  // hide inside a collapsed row. The card unions these into the open set and
  // re-adds them if the operator tries to collapse a still-dirty row.
  const coreForcedOpen = useRef<Set<string>>(new Set());
  const advancedForcedOpen = useRef<Set<string>>(new Set());

  const handleCoreForceOpen = useCallback((purpose: Purpose, force: boolean) => {
    if (force) {
      coreForcedOpen.current.add(purpose);
      setOpenCore((prev) => (prev.includes(purpose) ? prev : [...prev, purpose]));
    } else {
      coreForcedOpen.current.delete(purpose);
    }
  }, []);
  const handleAdvancedForceOpen = useCallback((purpose: Purpose, force: boolean) => {
    if (force) {
      advancedForcedOpen.current.add(purpose);
      setOpenAdvanced((prev) => (prev.includes(purpose) ? prev : [...prev, purpose]));
    } else {
      advancedForcedOpen.current.delete(purpose);
    }
  }, []);
  const handleCoreChange = useCallback((next: string[]) => {
    const merged = [...next];
    coreForcedOpen.current.forEach((p) => {
      if (!merged.includes(p)) merged.push(p);
    });
    setOpenCore(merged);
  }, []);
  const handleAdvancedChange = useCallback((next: string[]) => {
    const merged = [...next];
    advancedForcedOpen.current.forEach((p) => {
      if (!merged.includes(p)) merged.push(p);
    });
    setOpenAdvanced(merged);
  }, []);

  const hasAnyMapping = mappings.length > 0;
  // First-run state: zero mappings configured AND fetch resolved.
  const isFirstRun = !isLoading && !hasAnyMapping && !manualOpen;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product data lookup</CardTitle>
        <CardDescription>
          How we find print files from your Shopify products.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* BLU-536 — sides are DERIVED from design-ID presence (BLU-498 D4),
            never mapped directly; the second sentence is the operator-facing
            statement of that rule. ONE paragraph (Plan 08 / impeccable P3:
            the intro was two muted paragraphs + the sweep block before the
            first row — top-heavy). */}
        <p className="text-sm text-muted-foreground">
          We read codes like{' '}
          <span className="font-mono text-foreground">SAN14M</span>{' '}
          from your Shopify product fields to find the right print file
          when an order comes in. Print sides follow the design IDs you
          set: front only, back only, or both.
        </p>

        {isLoading && <LoadingSkeleton />}

        {/* Feature A — one-click definition provisioning. Admin-only (the
            block returns null otherwise). On first run this is the single
            primary CTA (the EmptyState's "Use recommended setup" button is
            hidden below); filling any mapping opens the Advanced disclosure
            since every preset purpose is advanced. */}
        {!isLoading && (
          <CreateDefinitionsBlock
            admin={admin}
            discoveryStatus={discoveryStatus}
            onAdvancedMappingsFilled={() => setAdvancedOpen(true)}
            onRetryDiscovery={handleRetryDiscovery}
          />
        )}

        {/*
          BLU-461 — SweepStatusBlock replaces the per-row Re-sync UI.
          Hidden in the empty/first-run state per frontend P0-4: there's
          nothing to sweep until at least one mapping is configured.
        */}
        {hasAnyMapping && <SweepStatusBlock admin={admin} />}

        {isFirstRun && (
          <EmptyState
            admin={admin}
            onConfigureManually={() => setManualOpen(true)}
          />
        )}

        {(hasAnyMapping || manualOpen) && !isLoading && (
          <div className="space-y-6">
            {/* Core 3 — flat, no group headings: their labels already say
                front / back / variant (Plan 07 review-pinned IA). Each row is
                a collapsed accordion; expanding reveals the bulk Test +
                per-SKU Test editor untouched. */}
            <Accordion
              type="multiple"
              value={openCore}
              onValueChange={handleCoreChange}
              className="space-y-4"
            >
              {CORE_PURPOSES.map((purpose) => {
                const existing = configuredByPurpose.get(purpose);
                return (
                  <MappingRow
                    key={purpose}
                    purpose={purpose}
                    admin={admin}
                    initialNamespace={existing?.namespace ?? ''}
                    initialKey={existing?.key ?? ''}
                    isPersisted={!!existing}
                    onSave={(input) =>
                      upsertMutation.mutateAsync({
                        purpose,
                        namespace: input.namespace,
                        key: input.key,
                      })
                    }
                    onDelete={
                      existing
                        ? async () => {
                            await deleteMutation.mutateAsync({ purpose });
                          }
                        : undefined
                    }
                    savePending={upsertMutation.isPending}
                    deletePending={deleteMutation.isPending}
                    discoveryStatus={discoveryStatus}
                    discoveredRows={discoveredRows}
                    onRetryDiscovery={handleRetryDiscovery}
                    onForceOpenChange={handleCoreForceOpen}
                    showTestAffordances
                  />
                );
              })}
            </Accordion>

            {/* BLU-536 Plan 08 — card-level dual-aware preview: one SKU in,
                BOTH composed filenames out (front + back through the real
                composition seam), with flagged rows naming any misconfigured
                metafield value. Replaces the per-row single-filename preview
                the old SkuTestSection band carried. */}
            <SkuPreviewPanel savedMappings={savedMappingsByPurpose} />

            {/* BLU-536 — ONE "Advanced print mapping" disclosure holding the
                8 new purposes, grouped Front / Back / Shared. Collapsible
                open/close motion is the canonical site-wide treatment from
                index.css ([data-radix-collapsible-content] grid transition,
                prefers-reduced-motion override built in) — same surface as
                the OrganizationSettings credentials Collapsible. The count
                badge sits INSIDE the trigger button so its text joins the
                accessible name ("Advanced print mapping, N configured"). */}
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                    Advanced print mapping
                    {configuredAdvancedCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] tabular-nums"
                      >
                        {configuredAdvancedCount} configured
                      </Badge>
                    )}
                  </span>
                  {advancedOpen ? (
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <div className="space-y-6">
                  <p className="text-xs text-muted-foreground">
                    Optional. Map these when a product&rsquo;s front and back
                    use different shapes, variants, or platen sizes. Anything
                    left unset keeps today&rsquo;s behaviour.
                  </p>
                  {/* One accordion spans every advanced group so a single
                      controlled value array tracks all open rows; the group
                      labels are non-item children between the AccordionItems. */}
                  <Accordion
                    type="multiple"
                    value={openAdvanced}
                    onValueChange={handleAdvancedChange}
                    className="space-y-6"
                  >
                    {ADVANCED_GROUPS.map((group) => (
                      <div
                        key={group.id}
                        role="group"
                        aria-label={`${group.label} advanced mappings`}
                        className="space-y-3"
                      >
                        {/* §13.4-style group label (location-switcher
                            treatment). */}
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {group.label}
                        </p>
                        <div className="space-y-4">
                          {group.purposes.map((purpose) => {
                            const existing = configuredByPurpose.get(purpose);
                            return (
                              <MappingRow
                                key={purpose}
                                purpose={purpose}
                                admin={admin}
                                initialNamespace={existing?.namespace ?? ''}
                                initialKey={existing?.key ?? ''}
                                isPersisted={!!existing}
                                onSave={(input) =>
                                  upsertMutation.mutateAsync({
                                    purpose,
                                    namespace: input.namespace,
                                    key: input.key,
                                  })
                                }
                                onDelete={
                                  existing
                                    ? async () => {
                                        await deleteMutation.mutateAsync({
                                          purpose,
                                        });
                                      }
                                    : undefined
                                }
                                savePending={upsertMutation.isPending}
                                deletePending={deleteMutation.isPending}
                                discoveryStatus={discoveryStatus}
                                discoveredRows={discoveredRows}
                                onRetryDiscovery={handleRetryDiscovery}
                                onForceOpenChange={handleAdvancedForceOpen}
                                // Interim safety (Plan 07 review BLOCKER fix):
                                // no Test / Test-with-SKU on advanced rows.
                                // Until Plan 06 lands, those testers resolve
                                // variant purposes product-level and report
                                // red on CORRECT configs; Plan 08 replaces
                                // them with the card-level dual preview.
                                showTestAffordances={false}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </Accordion>

                  {/* BLU-536 Plan 08 — read-only digest of the shared
                      default Design Shape map, next to the shape rows it
                      explains. */}
                  <DesignShapeMapSummary />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton — matches the eventual MappingRow shape so the layout
// doesn't jump when data lands.
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6 motion-safe:animate-pulse motion-reduce:animate-none">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="space-y-3 rounded-md border bg-card p-4 min-h-[10rem]"
          aria-hidden="true"
        >
          <div className="h-4 w-40 rounded bg-muted" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="h-3 w-20 rounded bg-muted" />
              <div className="h-9 rounded bg-muted" />
            </div>
            <div className="space-y-1">
              <div className="h-3 w-20 rounded bg-muted" />
              <div className="h-9 rounded bg-muted" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-16 rounded bg-muted" />
            <div className="h-8 w-16 rounded bg-muted" />
          </div>
        </div>
      ))}
      <span className="sr-only">Loading mappings…</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — first-run path (PLAN §9.3)
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  admin: boolean;
  onConfigureManually: () => void;
}

/**
 * Feature A first-run IA — the CreateDefinitionsBlock above this state is
 * the single primary CTA for admins, so this block carries GUIDANCE only
 * (which path fits which store) plus the "Or configure manually" escape
 * hatch. The old "Use recommended setup" core-3 seeding button was removed
 * with Feature A (it had become unreachable behind the block).
 */
function EmptyState({ admin, onConfigureManually }: EmptyStateProps) {
  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3 motion-safe:animate-in motion-safe:fade-in-50 motion-reduce:animate-none motion-reduce:transition-none">
      {admin ? (
        <p className="text-sm">
          The quickest start is &ldquo;Create definitions in Shopify&rdquo;
          above: it sets up the artwork, custom print SKU, fulfilment type,
          and pretreat fields and maps them here. Stores that keep design
          codes in fields like{' '}
          <span className="font-mono">my_fields.design_id</span> can map
          those manually instead.
        </p>
      ) : (
        <>
          <p className="text-sm">
            We need to know where your print data lives in Shopify.
          </p>
          {/* Coordinator dead-end exit at the empty state — they can't
              Save, so the primary CTA is the diagnostic copy instead. */}
          <p className="text-sm text-muted-foreground">
            Ask an admin to set this up.
          </p>
        </>
      )}
      <div className="flex flex-col items-start gap-2 pt-1">
        <button
          type="button"
          onClick={onConfigureManually}
          className="text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded motion-reduce:transition-none"
        >
          Or configure manually
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mapping row — one row per purpose (PLAN §9.4 + §9.6 + §9.8)
// ---------------------------------------------------------------------------

interface MappingRowProps {
  purpose: Purpose;
  admin: boolean;
  initialNamespace: string;
  initialKey: string;
  isPersisted: boolean;
  onSave: (input: { namespace: string; key: string }) => Promise<unknown>;
  onDelete?: () => Promise<void>;
  savePending: boolean;
  deletePending: boolean;
  // BLU-460 follow-up — discovery dropdown data threaded from the Card.
  discoveryStatus: DiscoveryStatus;
  discoveredRows: ReadonlyArray<DiscoveredMetafieldRow>;
  onRetryDiscovery: () => void;
  /**
   * BLU-536 interim safety — when false (advanced rows), neither the bulk
   * TestButton nor the per-SKU SkuTestSection renders. The existing testers
   * resolve variant purposes at PRODUCT level until Plan 06 lands, so they
   * would report red on correct advanced configs; Plan 08 replaces them
   * with a card-level front+back preview. Core-3 rows pass true and keep
   * their testers untouched.
   */
  showTestAffordances: boolean;
  /**
   * BLU-432 accordion pass — report an open hint (`true` while this row holds
   * unsaved edits or an invalid namespace/key) so the parent Accordion keeps
   * the row expanded and a validation error can't hide in a collapsed row.
   * Optional; omitting it disables the nicety.
   */
  onForceOpenChange?: (purpose: Purpose, force: boolean) => void;
}

function MappingRow({
  purpose,
  admin,
  initialNamespace,
  initialKey,
  isPersisted,
  onSave,
  onDelete,
  savePending,
  deletePending,
  discoveryStatus,
  discoveredRows,
  onRetryDiscovery,
  showTestAffordances,
  onForceOpenChange,
}: MappingRowProps) {
  const [namespace, setNamespace] = useState(initialNamespace);
  const [keyValue, setKeyValue] = useState(initialKey);

  const nsValid = namespace === '' || NS_KEY_REGEX.test(namespace);
  const keyValid = keyValue === '' || NS_KEY_REGEX.test(keyValue);
  const allValid = NS_KEY_REGEX.test(namespace) && NS_KEY_REGEX.test(keyValue);
  const dirty =
    isPersisted && (namespace !== initialNamespace || keyValue !== initialKey);
  const canSave = admin && allValid && (!isPersisted || dirty);

  const handleDelete = useCallback(() => {
    if (!onDelete) return;
    // Destructive action — confirm before firing. PLAN §9.7 + DESIGN.md
    // §15.5 "confirm destructive actions or provide Undo." We pick confirm
    // here over an inline AlertDialog because (a) Delete is rare and
    // (b) the modal overhead would obscure the row context.
    // `window.confirm` accepts plain text only, so curly quotes are
    // injected as literal Unicode characters (not HTML entities). Microcopy
    // hard rule from DESIGN.md §17.
    const ok = window.confirm(
      `Delete the ${PURPOSE_SHORT_LABEL[purpose]} mapping? This cannot be undone (re-add via “Use recommended setup” if needed).`,
    );
    if (!ok) return;
    void onDelete();
  }, [onDelete, purpose]);

  // Deep-link nicety (BLU-432): report "keep open" whenever this row holds
  // unsaved edits or an invalid namespace/key so the parent Accordion can't
  // collapse a validation error out of sight. Reports false when clean/valid
  // and on unmount.
  useEffect(() => {
    onForceOpenChange?.(purpose, dirty || !nsValid || !keyValid);
    return () => onForceOpenChange?.(purpose, false);
  }, [dirty, nsValid, keyValid, purpose, onForceOpenChange]);

  // Compact summary of the SAVED mapping (props, not the in-flight edit) so
  // the collapsed header always states what is actually live.
  const savedSummary = isPersisted ? `${initialNamespace}.${initialKey}` : null;

  return (
    <AccordionItem value={purpose} className="border rounded-lg bg-card px-3 sm:px-4">
      <AccordionTrigger
        type="button"
        className="py-3 min-h-[44px] gap-3 hover:no-underline hover:bg-muted/30 -mx-3 sm:-mx-4 px-3 sm:px-4 rounded-t-lg transition-colors data-[state=closed]:rounded-b-lg"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <span className="shrink-0 text-sm font-medium">{PURPOSE_LABEL[purpose]}</span>
          {isPersisted ? (
            <Badge variant="secondary" className="shrink-0 text-[10px] uppercase tracking-wide">
              Active
            </Badge>
          ) : (
            <Badge variant="outline" className="shrink-0 text-[10px] uppercase tracking-wide">
              Not set
            </Badge>
          )}
          {dirty && (
            <Badge
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-700 text-[10px] uppercase tracking-wide"
            >
              Unsaved
            </Badge>
          )}
          {/* Right-aligned summary of the mapped Shopify field, before the
              chevron the AccordionTrigger appends. */}
          <span className="ml-auto min-w-0 truncate pl-2 text-xs text-muted-foreground">
            {savedSummary ? (
              <span className="font-mono">{savedSummary}</span>
            ) : (
              'Not configured'
            )}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4 pt-1">
        <div className="space-y-3">
          {/*
            BLU-498 — structural "Per-variant" hint sits on the help line, not
            next to the state badge (which now lives in the trigger). Amber
            (DESIGN.md §1) is reserved for state/warning/attention, so we use a
            neutral muted-foreground treatment with a Layers icon that says
            "different kind of thing, not a status."
          */}
          <div className="flex items-start gap-1.5">
            {VARIANT_PURPOSES.has(purpose) && (
              <span
                className="mt-px inline-flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"
                role="note"
                aria-label="This metafield is set on each variant in Shopify, not on the product"
              >
                <Layers className="h-3 w-3" aria-hidden="true" />
                Per-variant
              </span>
            )}
            <p className="text-xs text-muted-foreground">{PURPOSE_HELP[purpose]}</p>
          </div>

          <MetafieldPicker
            purpose={purpose}
            namespace={namespace}
            keyValue={keyValue}
            editable={admin}
            busy={savePending}
            namespaceInvalid={!nsValid}
            keyInvalid={!keyValid}
            discoveryStatus={discoveryStatus}
            discoveredRows={discoveredRows}
            onRetryDiscovery={onRetryDiscovery}
            onPick={(ns, k) => {
              setNamespace(ns);
              setKeyValue(k);
            }}
            onNamespaceChange={setNamespace}
            onKeyChange={setKeyValue}
          />

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {showTestAffordances && (
              <TestButton namespace={namespace} keyValue={keyValue} disabled={!allValid} admin={admin} />
            )}
            {admin && (
              <Button
                type="button"
                size="sm"
                variant={dirty ? 'default' : 'outline'}
                onClick={() => {
                  if (!canSave) return;
                  void onSave({ namespace, key: keyValue }).then(() => {
                    if (!isPersisted) {
                      toast.success(
                        `${PURPOSE_SHORT_LABEL[purpose].charAt(0).toUpperCase()}${PURPOSE_SHORT_LABEL[purpose].slice(1)} mapping saved.`,
                      );
                    }
                  });
                }}
                disabled={!canSave || savePending}
              >
                {savePending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Saving&hellip;
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            )}
            {/* Delete moved out of the header into the expanded body (mirrors
                the production accordion: row-level actions live inside the
                content, not the trigger, which can't contain nested buttons).
                BLU-461 — per-row Re-sync button removed; operators trigger a
                full-org sweep via the SweepStatusBlock at the top of the card. */}
            {admin && isPersisted && onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={deletePending}
                aria-label={`Delete ${PURPOSE_SHORT_LABEL[purpose]} mapping`}
                className="ml-auto text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                Delete
              </Button>
            )}
          </div>

          {/* BLU-460 follow-up — per-SKU Test affordance sits below the
              existing TestButton. Operator types a SKU; the server resolves
              the variant + runs the mapping against THAT specific product.
              Stays independent from the bulk Test result band above.
              BLU-536 — hidden on advanced rows (interim safety; see
              showTestAffordances doc). */}
          {showTestAffordances && (
            <SkuTestSection
              purpose={purpose}
              namespace={namespace}
              keyValue={keyValue}
              mappingValid={allValid}
            />
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// ---------------------------------------------------------------------------
// Test button + result band (PLAN §9.4)
//
// Motion ordering (PLAN §9.4 v3.1 P0-3) drives the timing here:
//   - On click: phase = 'fetching' (spinner + disabled, label "Testing…").
//   - On response: phase = 'cooling' (keep spinner visible for the 150ms
//     spinner fade-out window). The result band has NOT mounted yet.
//   - After cooling: phase = 'showing' (band mounts → motion-safe animation
//     applies the 200ms fade+slide; aria-live announces post-mount).
//   - +200ms after band mount: phase = 'idle' (button re-enables).
// ---------------------------------------------------------------------------

interface TestButtonProps {
  namespace: string;
  keyValue: string;
  disabled: boolean;
  admin: boolean;
}

type TestSample = {
  sku: string | null;
  resolvedValue: string | null;
  productGid: string;
  title: string;
};

type TestResult = {
  sampleSize: number;
  resolvedCount: number;
  noProductsFound: boolean;
  namespace: string;
  key: string;
  samples: TestSample[];
};

function TestButton({ namespace, keyValue, disabled, admin }: TestButtonProps) {
  const [result, setResult] = useState<TestResult | null>(null);
  const { phase, start, reset, showWithMotion } = useStaggeredTestPhase();

  const testQuery = trpc.shopifyMetafieldMappings.testMapping.useQuery(
    { namespace, key: keyValue },
    {
      enabled: false, // imperative .refetch() — never auto-fire (PLAN §6.5 v3)
      retry: false,
    },
  );

  const handleClick = async () => {
    start();
    const res = await testQuery.refetch();
    if (res.error) {
      // Transient Test failures intentionally use a NON-persistent toast
      // (operator just re-clicks Test). Save/Delete/Re-sync errors are
      // persistent — see DESIGN.md §15.5 exception: "transient operator-
      // initiated checks can dismiss." Test fits the exception.
      toast.error(`Test failed: ${res.error.message}`, { id: TOAST_ID.testError });
      reset();
      return;
    }
    if (!res.data) {
      reset();
      return;
    }
    const next: TestResult = {
      sampleSize: res.data.sampleSize,
      resolvedCount: res.data.resolvedCount,
      noProductsFound: res.data.noProductsFound,
      namespace,
      key: keyValue,
      samples: res.data.samples.map((s) => ({
        sku: s.sku,
        resolvedValue: s.resolvedValue,
        productGid: s.productGid,
        title: s.title,
      })),
    };

    showWithMotion(() => {
      setResult(next);
    });
  };

  const buttonBusy = phase === 'fetching' || phase === 'cooling';

  return (
    <div className="w-full space-y-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={disabled || buttonBusy || phase === 'showing'}
      >
        {buttonBusy ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            Testing&hellip;
          </>
        ) : (
          'Test'
        )}
      </Button>
      {/* Single mount per test result. The `key` is the result identity —
          a NEW result causes a fresh element (animation replays); a phase
          transition (`showing → idle`) on the SAME result keeps the same
          element so `aria-live` doesn't re-announce. Earlier two-branch
          conditional double-mounted and triggered SR re-reads on every
          phase tick. */}
      {result && (
        <TestResultBand
          key={`${result.namespace}-${result.key}-${result.sampleSize}-${result.resolvedCount}`}
          result={result}
          showCoordinatorExit={!admin}
          animateIn={phase === 'showing'}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test result band (4 semantic states per PLAN §9.4)
// ---------------------------------------------------------------------------

interface TestResultBandProps {
  result: TestResult;
  showCoordinatorExit: boolean;
  /** Apply the entrance animation classes. Defaults to true. */
  animateIn?: boolean;
}

function TestResultBand({ result, showCoordinatorExit, animateIn = true }: TestResultBandProps) {
  const { sampleSize, resolvedCount, noProductsFound, namespace, key, samples } = result;

  let kind: BandKind;
  let icon: React.ReactNode;
  let headline: string;

  if (noProductsFound) {
    kind = 'gray';
    icon = <Info className="h-4 w-4" aria-hidden="true" />;
    headline =
      'No products in your Shopify store yet. You can save this mapping; we’ll use it when products are added.';
  } else if (resolvedCount === sampleSize && sampleSize > 0) {
    kind = 'green';
    icon = <CheckCircle2 className="h-4 w-4" aria-hidden="true" />;
    headline = `${resolvedCount} of ${sampleSize} resolved.`;
  } else if (resolvedCount === 0) {
    kind = 'red';
    icon = <X className="h-4 w-4" aria-hidden="true" />;
    headline = `Found products but none had ${namespace}.${key} set. Check the namespace and key.`;
  } else if (resolvedCount === 1) {
    kind = 'amber';
    icon = <AlertCircle className="h-4 w-4" aria-hidden="true" />;
    headline = `Only 1 of ${sampleSize} resolved. Check the mapping or add product fields.`;
  } else {
    kind = 'amber';
    icon = <AlertCircle className="h-4 w-4" aria-hidden="true" />;
    headline = `${resolvedCount} of ${sampleSize} resolved.`;
  }

  // Palette + animation + role="status" shell live in the shared ResultBand
  // (Plan 08 — single-sourced; see ResultBand.tsx).
  const [expanded, setExpanded] = useState(false);

  return (
    <ResultBand kind={kind} animateIn={animateIn}>
      <div className="flex items-start gap-2 min-w-0">
        <span className="mt-0.5">{icon}</span>
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-medium leading-tight tabular-nums">{headline}</p>
          {kind === 'green' && samples[0] && samples[0].resolvedValue && samples[0].sku && (
            <p className="font-mono text-xs tabular-nums opacity-90 truncate">
              {samples[0].sku} to {samples[0].resolvedValue}
            </p>
          )}
        </div>
      </div>

      {samples.length > 0 && resolvedCount < sampleSize && !noProductsFound && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
          >
            {expanded ? (
              <ChevronUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            )}
            {expanded ? 'Hide samples' : 'Show samples'}
          </button>
          {expanded && (
            <ul className="space-y-1 pl-1 text-xs font-mono tabular-nums">
              {samples.map((s, i) => (
                <li
                  key={s.productGid + i}
                  className="flex justify-between gap-3 min-w-0"
                >
                  <span className="truncate min-w-0">{s.sku ?? '(no SKU)'}</span>
                  <span className="opacity-80 shrink-0">
                    {s.resolvedValue ?? '(no value)'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showCoordinatorExit && (kind === 'amber' || kind === 'red') && (
        <CoordinatorExit result={result} />
      )}
    </ResultBand>
  );
}

// ---------------------------------------------------------------------------
// Coordinator dead-end exit (PLAN §9.5)
// ---------------------------------------------------------------------------

interface CoordinatorExitProps {
  result: TestResult;
}

function CoordinatorExit({ result }: CoordinatorExitProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const lines = [
      'Product data lookup: diagnostic',
      `Mapping: ${result.namespace}.${result.key}`,
      `Test result: ${result.resolvedCount} of ${result.sampleSize} resolved`,
      'Failing samples:',
      ...result.samples
        .filter((s) => !s.resolvedValue)
        .map(
          (s) =>
            `  - SKU ${s.sku ?? '(no SKU)'}, GID ${s.productGid} (no value)`,
        ),
      `Generated: ${new Date().toISOString()}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      toast.error('Copy failed. Select the text manually.', { id: TOAST_ID.copyError });
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2 text-sm">
      <span className="italic">Ask an admin to update this mapping.</span>
      <Button type="button" size="sm" variant="outline" onClick={handleCopy}>
        {copied ? (
          <>
            <Check className="mr-1 h-3 w-3" aria-hidden="true" />
            Copied
          </>
        ) : (
          <>
            <Clipboard className="mr-1 h-3 w-3" aria-hidden="true" />
            Copy diagnostic
          </>
        )}
      </Button>
      {/* Separate live region so the announcement is decoupled from
          the button's static label — placing aria-live on the button
          itself would announce its label on every render. */}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? 'Diagnostic copied to clipboard.' : ''}
      </span>
    </div>
  );
}

// BLU-461 — Per-row Re-sync UI removed. The Phase B sync architecture
// pivot replaced this affordance with a card-top SweepStatusBlock + the
// cron-driven org-wide sweep. See `./product-data-lookup/SweepStatusBlock.tsx`.
